import React, { FC, useState, useEffect, ChangeEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import orderBy from 'lodash/orderBy';
import { TextField, Button, Box, FormHelperText } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

import { reduxActions, useDispatch } from 'store';
import { ILanguageListItem } from 'api/types';
import { IDynamicUiField, ITranslation } from 'plugins/api/types';

import { Modal } from 'components/controls';

interface IProps {
  field: IDynamicUiField;
  languagesData?: ILanguageListItem[];
  pageSymbol: string;
  componentSymbol: string;
}

const TEXT_AREA_MAX_HEIGHT = 200;

const textAreaSx = (minHeight?: number, adjoinButton?: boolean): SxProps<Theme> => ({
  '& .MuiInputLabel-root': { lineHeight: 1 },
  '& .MuiInputLabel-root.Mui-focused': { lineHeight: 'normal' },
  '& .MuiOutlinedInput-root': {
    padding: '12px 4px 12px 14px',
    alignItems: 'flex-start',
    borderRadius: adjoinButton ? '4px 0 0 4px' : '4px',
    overflow: 'visible',
    ...(minHeight != null && minHeight > 0 ? { minHeight } : {})
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '0 4px',
    borderRadius: '4px',
    boxSizing: 'border-box',
    maxHeight: `${TEXT_AREA_MAX_HEIGHT}px`,
    minHeight: 0,
    overflow: 'auto !important',
    resize: 'none'
  }
});

const textAreaInputProps = {
  style: {
    maxHeight: TEXT_AREA_MAX_HEIGHT,
    overflowY: 'auto' as const,
    overflowX: 'hidden' as const,
    boxSizing: 'border-box' as const
  }
};

const DynamicTranslation: FC<IProps> = ({ field, languagesData, pageSymbol, componentSymbol }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const getPosition = (string: string, subString: string, index: number) => {
    return string.split(subString, index).join(subString).length;
  };

  const urlPrefix = pathname.slice(0, getPosition(pathname, '/', 2));

  const [value, setValue] = useState(field.value);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (field.value && pageSymbol && componentSymbol) {
      dispatch(
        reduxActions.setFieldValue({
          pageSymbol,
          componentSymbol,
          fieldName: field.field_symbol,
          fieldValue: field.value
        })
      );
    }
  }, [field.value]);

  useEffect(() => {
    if (field.value && !!field.value.length) {
      setValue(field.value);

      return;
    }

    if (languagesData) {
      const languages = languagesData.map((language) => ({
        language: language.id,
        translation: ''
      }));
      setValue(languages as any);
    }
  }, [field.value, languagesData]);

  const onChangeHandler = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    language: string
  ) => {
    if (value && Array.isArray(value)) {
      const newValue: ITranslation[] = value.map((o) => {
        if (o.language === language) {
          return {
            ...o,
            translation: e.target.value
          };
        }

        return o;
      });

      dispatch(
        reduxActions.setFieldValue({
          pageSymbol,
          componentSymbol,
          fieldName: field.field_symbol,
          fieldValue: newValue
        })
      );

      setValue(newValue as any);
    }
  };

  const renderContent = () => {
    return (
      value &&
      Array.isArray(value) &&
      value.map((o, i: number) => (
        <Box key={i} sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={3}
            color="secondary"
            sx={textAreaSx(field.height)}
            inputProps={textAreaInputProps}
            label={String(o.language)}
            value={o.translation}
            onChange={(e) => onChangeHandler(e, o.language)}
            disabled={!field.editable}
          />
        </Box>
      ))
    );
  };

  const fieldToDisplay = Array.isArray(value)
    ? orderBy(value, (a) => a.language !== urlPrefix.replace('/', ''))
    : [];

  return (
    <Box className="dynamicText">
      {fieldToDisplay.length > 0 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 0, alignItems: 'stretch', width: '100%' }}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              color="secondary"
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                alignSelf: 'flex-start',
                ...textAreaSx(field.height, true)
              }}
              inputProps={textAreaInputProps}
              label={field.label}
              value={fieldToDisplay[0].translation}
              onChange={(e) => onChangeHandler(e, fieldToDisplay[0].language)}
              disabled={!field.editable}
            />
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setOpen(true)}
              sx={{
                flexShrink: 0,
                alignSelf: 'stretch',
                minWidth: 56,
                px: 1.5,
                borderRadius: '0 4px 4px 0',
                borderLeft: 'none',
                ml: '-1px',
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              {fieldToDisplay[0].language}
            </Button>
          </Box>
          {field.hint ? <FormHelperText sx={{ mx: 1.75, mt: 0.5 }}>{field.hint}</FormHelperText> : null}
        </Box>
      )}
      {open && (
        <Modal onClose={() => setOpen(false)} title={t('Słownik')}>
          <Box sx={{ pt: 0.5 }}>{renderContent()}</Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="secondary" onClick={() => setOpen(false)}>
              <Trans>Zamknij</Trans>
            </Button>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

export default DynamicTranslation;
